import { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar'
import moment from 'moment'
import "moment/locale/es"
import "react-big-calendar/lib/css/react-big-calendar.css";

import { useAgenda } from './hooks/useAgenda';
import { useCitaAtender } from './hooks/useCitaAtender';
import { useCitaConfirmar } from './hooks/useCitaConfirmar';
import { useCitaSolicitarConfirmacion } from './hooks/useCitaSolicitarConfirmacion';
import { useCitaCancelar } from './hooks/useCitaCancelar';
import { useCitaNoPresentar } from './hooks/useCitaNoPresentar';
import { useCitaEliminar } from './hooks/useCitaEliminar';

import { setDay, setMonth, setYear } from '../../redux/slices/ui';

import { Event } from './components/Event';
import { Error, Loader, ModalContainer } from '../../components';
import { CitaAgendaModal } from '../Modals/CitaAgenda';
import { CitaPacienteModal } from '../Modals/CitaPaciente';
import { CrearCitaAgendaModal } from '../Modals/CrearCitaAgenda';
import { EventoAgendaModal } from '../Modals/EventoAgenda';
import { CrearEventoAgendaModal } from '../Modals/CrearEventoAgenda';
import { EditarEventoModal } from '../Modals/EditarEventoAgenda';
import { SelectorAgendaModal } from '../Modals/SelectorAgenda';

import styles from './Agenda.module.css';
import { colors } from '../../types/types';

const css = `
    .rbc-btn-group button {
      background-color: #FFF;
      color: ${ colors.PRIMARY };
      border-color: ${ colors.PRIMARY };
      padding: 8px 12px;
      border-radius: var(--bs-border-radius);
      margin: 0 4px;
      cursor: pointer;
      font-weight: 500;
    }

    .rbc-btn-group button.rbc-active {
      color: #FFF !important;
      border-color: ${ colors.PRIMARY };
      background-color: ${ colors.PRIMARY };
    }

    .rbc-btn-group button:hover {
      color: #FFF;
      border-color: ${ colors.PRIMARY };
      background-color: ${ colors.PRIMARY };
    }

    button.rbc-active:hover, .rbc-toolbar button.rbc-active:focus {
      color: #FFF;
      background-color: ${ colors.PRIMARY };
    }

    .rbc-toolbar-label {
      font-family: 'Roboto';
      font-weight: 400;
      font-size: 18px;
      line-height: 24px;
      color: #000;
      text-align: center;
    }

    .rbc-btn-group button.rbc-active {
      color: #FFF !important;
      background-color: ${ colors.PRIMARY } !important;
    }

    .rbc-btn-group button:focus {
      outline: none;
      box-shadow: 0 0 0 2px ${ colors.PRIMARY };
    }

    .rbc-btn-group button:active {
      color: #FFF !important;
      background-color: ${ colors.PRIMARY } !important;
    }

    .rbc-btn-group button.rbc-active {
      color: #FFF !important;
      background-color: ${ colors.PRIMARY } !important;
    }

    .rbc-toolbar button:focus {
      color: #FFF;
      background-color: ${ colors.PRIMARY } !important;
      color: #FFF;
      border-color: ${ colors.PRIMARY };
    }

    .rbc-btn-group button:focus {
      outline: none;
      box-shadow: none;
    }

    .rbc-events-container, .rbc-month-row {
      cursor: pointer !important;
    }

    /* En time views (día/semana), oculta la hora nativa de RBC */
    .rbc-time-view .rbc-event .rbc-event-label {
      display: none !important;
    }
`;

export const Agenda = () => {

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { permisos } = useSelector(state => state.auth);

  const { day, month, year } = useSelector(state => state.ui.fecha);

  const formats = {
    dayRangeHeaderFormat: ({ start, end }) => {
      const texto = moment(start).format("MMMM D, YYYY");
      return texto.charAt(0).toUpperCase() + texto.slice(1);
    },
    dayHeaderFormat: (date) => {
      const texto = moment(date).format("MMMM D, YYYY");
      return texto.charAt(0).toUpperCase() + texto.slice(1);
    },
  };

  moment.locale("es");
  const localizer = momentLocalizer(moment);

  const [currentDate, setCurrentDate] = useState(new Date(year, month - 1, day));

  const [currentView, setCurrentView] = useState(Views.DAY);

  const [viewCitaModal, setViewCitaModal] = useState(null);
  const [editarCitaModal, setEditarCitaModal] = useState(null);

  const [viewEventoModal, setViewEventoModal] = useState(null);
  const [editarEventoModal, setEditarEventoModal] = useState(null);

  const [start, setStart] = useState(`${year}-${month}-${day}`);

  const [end, setEnd] = useState(`${year}-${month}-${day}`);

  const [statusCrearCitaModal, setStatusCrearCitaModal] = useState(false);
  const [crearCitaModalData, setCrearCitaModalData] = useState(null);

  const [statusSelectorModal, setStatusSelectorModal] = useState(null);

  const [statusCrearEventoModal, setStatusCrearEventoModal] = useState(false);
  const [crearEventoModalData, setCrearEventoModalData] = useState(null);

  const { agendaQuery: { isLoading, isError, data: { citas: events } } } = useAgenda(start, end);

  const { confirmarCitaMutation: { mutate: confirmarCita } } = useCitaConfirmar();

  const { solicitarConfirmacionCitaMutation: { mutate: solicitarConfirmacionCita } } = useCitaSolicitarConfirmacion();

  const { atenderCitaMutation: { mutate: atenderCita } } = useCitaAtender();

  const { cancelarCitaMutation: { mutate: cancelarCita } } = useCitaCancelar();
  
  const { noPresentarCitaMutation: { mutate: noPresentarCita } } = useCitaNoPresentar();

  const { eliminarCitaMutation: { mutate: eliminarCita } } = useCitaEliminar();

  const handleOnNavigate = (date) => {
    const dia = String(date.getDate()).padStart(2, "0");
    const mes = String(date.getMonth() + 1).padStart(2, "0");
    const año = String(date.getFullYear());
    dispatch(setDay(dia));
    dispatch(setMonth(mes));
    dispatch(setYear(año));
    setCurrentDate(date);
  }

  const handleViewCita = (cita) => {
    setViewCitaModal(cita);
  }

  const handleCancelarViewCita = () => {
    setViewCitaModal(null);
  }

  const handleViewEvento = (cita) => {
    setViewEventoModal(cita);
  }

  const handleCancelarViewEvento = () => {
    setViewEventoModal(null);
  }

  const handleAtenderCita = ({ id, paciente }) => {
    atenderCita({ id: id, paciente: paciente, success: (pacienteAux) => {
      handleCancelarViewCita();
      navigate(`/paciente/consulta/${pacienteAux}`);
    }, error: handleCancelarViewCita });
  }

  const handleConfirmarCita = ({ id, paciente }) => {
    confirmarCita({ id: id, paciente: paciente, success: handleCancelarViewCita, error: handleCancelarViewCita });
  }

  const handleSolicitarConfirmacionCita = ({ id }) => {
    solicitarConfirmacionCita({ id: id, success: handleCancelarViewCita, error: handleCancelarViewCita });
  }

  const handleEditarCita = (cita) => {
    setEditarCitaModal(cita);
    handleCancelarViewCita();
  }

  const handleCancelarEditarCita = () => {
    setEditarCitaModal(null);
  }

  const handleEditarEvento = (cita) => {
    setEditarEventoModal(cita);
    handleCancelarViewEvento();
  }

  const handleCancelarEditarEvento = () => {
    setEditarEventoModal(null);
  }

  const handleCancelarCita = ({ id, paciente }) => {
    cancelarCita({ id: id, paciente: paciente, success: handleCancelarViewCita, error: handleCancelarViewCita });
  }

  const handleNoPresentarCita = ({ id, paciente }) => {
    noPresentarCita({ id: id, paciente: paciente, success: handleCancelarViewCita, error: handleCancelarViewCita });
  }

  const handleEliminarEvento = ({ id, paciente }) => {
    eliminarCita({ id: id, paciente: paciente, success: handleCancelarViewEvento, error: handleCancelarViewEvento });
  }

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  useEffect(() => {
    const fecha = new Date(year, month - 1, day);
    handleOnNavigate(fecha);

    let start, end;
    switch (currentView) {
      case Views.MONTH:
        start = moment(fecha).startOf('month').toDate();
        end = moment(fecha).endOf('month').toDate();
        break;
      case Views.WEEK:
        start = moment(fecha).startOf('week').toDate();
        end = moment(fecha).endOf('week').toDate();
        break;
      case Views.DAY:
        start = moment(fecha).startOf('day').toDate();
        end = moment(fecha).endOf('day').toDate();
        break;
      default:
        start = moment(fecha).toDate();
        end = moment(fecha).toDate();
    }

    setStart(moment(start).format('YYYY-MM-DD'));
    setEnd(moment(end).format('YYYY-MM-DD'));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day, month, year])

  const convertUTCToFakeLocal = (isoString) => {
    const [year, month, day, hour, minute] = isoString
      .replace('Z', '')         // quita la Z
      .split(/[-T:.]/)          // separa componentes
      .map(Number);

    return new Date(year, month - 1, day, hour, minute); // Date interpretado como local
  };

  const handleSelectSlot = ({ start, end }) => {

    // Formato para fecha de cita
    const startFormato = start.toISOString().split("T")[0];

    // Formato para hora de cita
    const hours = String(start.getHours()).padStart(2, "0");
    const minutes = String(start.getMinutes()).padStart(2, "0");
    const horaFormato = `${hours}:${minutes}`;

    // Formato para fecha final de cita
    // const endFormato = end.toISOString().split("T")[0];

    // Formato para hora de cita
    // const hoursEnd = String(end.getHours()).padStart(2, "0");
    // const minutesEnd = String(end.getMinutes()).padStart(2, "0");
    // const horaEndFormato = `${hoursEnd}:${minutesEnd}`;

    // Duración de cita
    const duracion = (end - start) / (1000 * 60);

    setStatusCrearCitaModal(true);
    setCrearCitaModalData({ fecha: startFormato, hora: horaFormato, duracion });

    // setStatusSelectorModal({
    //   startFormato,
    //   horaFormato,
    //   duracion,
    //   fechaInicio: startFormato,
    //   horaInicio: horaFormato,
    //   fechaFin: endFormato,
    //   horaFin: horaEndFormato,
    // });

  };

  const hideSelectorModal = () => {
    setStatusSelectorModal(false);
  }

  const handleCrearCita = (fecha, hora, duracion) => {
    setStatusSelectorModal(null);
    setStatusCrearCitaModal(true);
    setCrearCitaModalData({ fecha, hora, duracion });
  }

  const hideCancelarCrearCita = () => {
    setStatusCrearCitaModal(false);
    setCrearCitaModalData(null);
  }

  const handleCrearEvento = (fechaInicio, horaInicio, fechaFin, horaFin) => {
    setStatusSelectorModal(null);
    setStatusCrearEventoModal(true);
    setCrearEventoModalData({ fechaInicio, horaInicio, fechaFin, horaFin });
  }

  const hideCancelarCrearEvento = () => {
    setStatusCrearEventoModal(false);
    setCrearEventoModalData(null);
  }

  const handleViewPaciente = (id) => {
    navigate(`/paciente/view/${id}`);
  }

  if(!permisos.agenda){

    return <Navigate to="" />

  }
  
  return (
    <>
      <style>{ css }</style>
      {(isLoading)
      ? <Loader />
      : <>
        {(isError)
          ? <Error />
          : <>
            <div className={"pt-3 pb-3 " + styles.calendarContainer}>
              <Calendar
                components={{
                  event: (rbcProps) => (
                    <Event
                      {...rbcProps}
                      currentView={currentView}
                    />
                  ),
                }}
                date={currentDate}
                defaultView="day"
                endAccessor="end"
                eventPropGetter={(event) => ({
                  style: {
                    backgroundColor: '#FFF',
                    // borderLeft: `10px solid ${event.colorCita}`,
                    padding: '2px 4px',
                    color: '#333',
                    fontSize: '0.85rem',
                  },
                })}
                events={events.map(evento => ({
                  ...evento,
                  start: convertUTCToFakeLocal(evento.start),
                  end: convertUTCToFakeLocal(evento.end),
                }))}
                formats={formats}
                localizer={localizer}
                messages={{
                  today: 'Hoy',
                  previous: 'Anterior',
                  next: 'Siguiente',
                  month: 'Mes',
                  week: 'Semana',
                  day: 'Día',
                  agenda: 'Agenda',
                }}
                onNavigate={handleOnNavigate}
                // onRangeChange={handleRangeChange}
                onSelectEvent={(event) => (event.evento) ? ((permisos.eventoRead || permisos.eventoUpdate || permisos.eventoDelete) ? handleViewEvento(event) : () => {}) : ((permisos.citaRead || permisos.citaUpdate || permisos.citaDelete) ? handleViewCita(event) : () => {})}
                onSelectSlot={(permisos.citaCreate) ? handleSelectSlot : () => {}}
                onView={handleViewChange}
                selectable={true}
                startAccessor="start"
                style={{ height: "100%" }}
                views={['day', 'week', 'month']}
              />
            </div>
            {((permisos.citaRead || permisos.citaUpdate || permisos.citaDelete) && viewCitaModal != null) &&
            <ModalContainer
              state={ viewCitaModal != null }
              hide={ handleCancelarViewCita }
              content={ <CitaAgendaModal
                hide={handleCancelarViewCita}
                cita={viewCitaModal}
                atenderCita={(permisos.citaUpdate) ? () => handleAtenderCita({
                  id: viewCitaModal.id,
                  paciente: (typeof viewCitaModal.paciente !== 'undefined') ? viewCitaModal.paciente._id : '0'
                }) : () => {}}
                confirmarCita={(permisos.citaUpdate) ? () => handleConfirmarCita({
                  id: viewCitaModal.id,
                  paciente: (typeof viewCitaModal.paciente !== 'undefined') ? viewCitaModal.paciente._id : '0'
                }) : () => {}}
                SolicitarConfirmacionCita={(permisos.citaUpdate) ? () => handleSolicitarConfirmacionCita({
                  id: viewCitaModal.id
                }) : () => {}}
                editarCita={(permisos.citaUpdate) ? () => handleEditarCita({
                  _id: viewCitaModal.id,
                  atendida: viewCitaModal.atendida,
                  confirmada: viewCitaModal.confirmada,
                  consultorio: viewCitaModal.consultorio,
                  dia: viewCitaModal.dia,
                  diaSinFormato: viewCitaModal.diaSinFormato,
                  duracion: viewCitaModal.duracion,
                  etiqueta: viewCitaModal.etiquetaCita,
                  etiquetaColor: viewCitaModal.colorCita,
                  hora: viewCitaModal.hora,
                  horaSinFormato: viewCitaModal.horaSinFormato,
                  inicio: viewCitaModal.start,
                  isPrimeraVez: viewCitaModal.isPrimeraVez,
                  motivo: viewCitaModal.motivo,
                  paciente: (typeof viewCitaModal.paciente !== 'undefined') ? {
                    _id: viewCitaModal.paciente._id,
                    nombre: viewCitaModal.paciente.nombre
                  } : {
                    _id: '0',
                    nombre: ''
                  },
                  nombre: (typeof viewCitaModal.datosCita.nombre !== 'undefined') ? viewCitaModal.datosCita.nombre : '',
                  apellidoPaterno: (typeof viewCitaModal.datosCita.apellidoPaterno !== 'undefined') ? viewCitaModal.datosCita.apellidoPaterno : '',
                  apellidoMaterno: (typeof viewCitaModal.datosCita.apellidoMaterno !== 'undefined') ? viewCitaModal.datosCita.apellidoMaterno : '',
                  fechaNacimiento: (typeof viewCitaModal.datosCita.fechaNacimiento !== 'undefined') ? viewCitaModal.datosCita.fechaNacimiento : '',
                  genero: (typeof viewCitaModal.datosCita.genero !== 'undefined') ? viewCitaModal.datosCita.genero : '',
                  email: (typeof viewCitaModal.datosCita.email !== 'undefined') ? viewCitaModal.datosCita.email : '',
                  celular: (typeof viewCitaModal.datosCita.celular !== 'undefined') ? viewCitaModal.datosCita.celular : '',
                  comentario: (typeof viewCitaModal.comentario !== 'undefined') ? viewCitaModal.comentario : '',
                }) : () => {}}
                cancelarCita={(permisos.citaUpdate) ? () => handleCancelarCita({
                  id: viewCitaModal.id,
                  paciente: (typeof viewCitaModal.paciente !== 'undefined') ? viewCitaModal.paciente._id : '0'
                }) : () => {}}
                noPresentarCita={(permisos.citaUpdate) ? () => handleNoPresentarCita({
                  id: viewCitaModal.id,
                  paciente: (typeof viewCitaModal.paciente !== 'undefined') ? viewCitaModal.paciente._id : '0'
                }) : () => {}}
                viewPaciente={(permisos.pacienteRead) ? () => handleViewPaciente((typeof viewCitaModal.paciente !== 'undefined') ? viewCitaModal.paciente._id : '0') : () => {}}
              /> }
            />
            }
            {((permisos.eventoRead || permisos.eventoUpdate || permisos.eventoDelete) && viewEventoModal != null) &&
            <ModalContainer
              state={ viewEventoModal != null }
              hide={ handleCancelarViewEvento }
              content={ <EventoAgendaModal
                hide={handleCancelarViewEvento}
                cita={viewEventoModal}
                editarCita={(permisos.eventoUpdate) ? () => handleEditarEvento({
                  _id: viewEventoModal.id,
                  atendida: viewEventoModal.atendida,
                  confirmada: viewEventoModal.confirmada,
                  consultorio: viewEventoModal.consultorio,
                  dia: viewEventoModal.dia,
                  diaSinFormato: viewEventoModal.diaSinFormato,
                  hora: viewEventoModal.hora,
                  horaSinFormato: viewEventoModal.horaSinFormato,
                  diaFin: viewEventoModal.diaFin,
                  diaSinFormatoFin: viewEventoModal.diaSinFormatoFin,
                  horaFin: viewEventoModal.horaFin,
                  horaSinFormatoFin: viewEventoModal.horaSinFormatoFin,
                  duracion: viewEventoModal.duracion,
                  etiqueta: viewEventoModal.etiquetaCita,
                  etiquetaColor: viewEventoModal.colorCita,
                  inicio: viewEventoModal.start,
                  nombre: (typeof viewEventoModal.datosCita.nombre !== 'undefined') ? viewEventoModal.datosCita.nombre : '',
                  comentario: (typeof viewEventoModal.comentario !== 'undefined') ? viewEventoModal.comentario : '',
                }) : () => {}}
                eliminarCita={(permisos.eventoDelete) ? () => handleEliminarEvento({
                  id: viewEventoModal.id,
                  paciente: '0'
                }) : () => {}}
              /> }
            />
            }
            {(permisos.citaUpdate && editarCitaModal != null) &&
            <ModalContainer
              state={ editarCitaModal != null }
              hide={ handleCancelarEditarCita }
              content={ <CitaPacienteModal hide={ handleCancelarEditarCita } paciente={editarCitaModal.paciente._id} cita={editarCitaModal} posponer={false} /> }
            />
            }
            {(permisos.eventoUpdate && editarEventoModal != null) &&
            <ModalContainer
              state={ editarEventoModal != null }
              hide={ handleCancelarEditarEvento }
              content={ <EditarEventoModal hide={ handleCancelarEditarEvento } cita={editarEventoModal} /> }
            />
            }
            {(permisos.citaCreate && statusCrearCitaModal) &&
            <ModalContainer
              state={ statusCrearCitaModal }
              hide={ hideCancelarCrearCita }
              content={ <CrearCitaAgendaModal data={crearCitaModalData} hide={ hideCancelarCrearCita } /> }
            />
            }
            {(permisos.eventoCreate && statusCrearEventoModal) &&
            <ModalContainer
              state={ statusCrearEventoModal }
              hide={ hideCancelarCrearEvento }
              content={ <CrearEventoAgendaModal data={crearEventoModalData} hide={ hideCancelarCrearEvento } /> }
            />
            }
            {((permisos.citaCreate || permisos.eventoCreate) && statusSelectorModal !== null) &&
            <ModalContainer
              state={ statusSelectorModal }
              hide={ hideSelectorModal }
              content={ <SelectorAgendaModal hide={ hideSelectorModal } evento={(permisos.eventoCreate) ? () => { handleCrearEvento(statusSelectorModal.fechaInicio, statusSelectorModal.horaInicio, statusSelectorModal.fechaFin, statusSelectorModal.horaFin); } : () => {}} cita={(permisos.citaCreate) ? () => { handleCrearCita(statusSelectorModal.startFormato, statusSelectorModal.horaFormato, statusSelectorModal.duracion); } : () => {}} /> }
            />
            }
          </>
        }
      </>
      }
    </>
  )
}
